import {
  Button,
  FormLabel,
  FormControl,
  Input,
  useDisclosure,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  Flex,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Code,
} from "@chakra-ui/react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useState } from "react";
import { apiRequest } from "utils/api";
import LocationAutocomplete from "../../components/elements/LocationAutocomplete";
import { hasPermission } from "utils/permissionHelper";
import { getErrorMessage } from "utils/errorHandler";
import { EntitySelect } from "components/loans/CreateNewLoanApplication/components/EntitySelect";
import { BorrowerCompanySelect } from "components/loans/CreateNewLoanApplication/components/BorrowerCompanySelect";

const AddPropertyButton = ({ onSuccess }: { onSuccess: () => void }) => {
  const { showSuccess, showError } = useContext(AlertsContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useTenant();
  const [name, setName] = useState("");
  const [address, setAddress] = useState<any>({}); // For JSON
  const [purchase_date, setPurchaseDate] = useState<string>("");
  const [completion_date, setCompletionDate] = useState<string>("");
  const [entityId, setEntityId] = useState<string | undefined>();
  const [borrowerCompanyId, setBorrowerCompanyId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const isReadPropertyAllowed = hasPermission(user, "read_property");

  const insertProperty = async () => {
    try {
      if (!name || !borrowerCompanyId || !purchase_date || !completion_date) {
        showError({
          title: "Missing Required Fields",
          description: "Please fill in all required fields.",
        });
        return;
      }

      setLoading(true);
      await apiRequest("/supabase/properties/insert", {
        name,
        purchase_date,
        completion_date,
        address: address.formatted,
        borrower_company: borrowerCompanyId,
        entity: entityId,
      });

      onSuccess();
      onClose();
      showSuccess({
        title: "Property Added",
        description: "The property has been successfully added.",
      });
    } catch (err) {
      console.log(err);
      showError({
        title: "Add Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBorrowerCompanyChange = (borrowerCompany: string | undefined) => {
    setBorrowerCompanyId(borrowerCompany);
    setEntityId(undefined);
  };

  return (
    <>
      <Button
        data-cy="new-properties"
        onClick={onOpen}
        variant="solid"
        leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
        isDisabled={isReadPropertyAllowed}
      >
        Add Property
      </Button>

      <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Add Property</DrawerHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <FormControl isRequired>
                <FormLabel>Property Name</FormLabel>
                <Input
                  placeholder="Property Name"
                  value={name}
                  onChange={ev => setName(ev.target.value)}
                  variant="primary"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Borrower Company</FormLabel>
                <BorrowerCompanySelect
                  borrowerCompanyId={borrowerCompanyId}
                  setBorrowerCompanyId={handleBorrowerCompanyChange}
                />
              </FormControl>
              {borrowerCompanyId && (
                <FormControl>
                  <FormLabel>Entity</FormLabel>
                  <EntitySelect
                    entityId={entityId}
                    setEntityId={setEntityId}
                    companyId={borrowerCompanyId}
                  />
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Property Address</FormLabel>
                <Box>
                  <LocationAutocomplete onChange={(location: any) => setAddress(location)} />
                </Box>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Purchase Date</FormLabel>
                <Input
                  placeholder="purchase date"
                  value={purchase_date !== null ? purchase_date : ""}
                  onChange={ev => setPurchaseDate(ev.target.value)}
                  variant="primary"
                  type="date"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Completion Date</FormLabel>
                <Input
                  placeholder="completion date"
                  value={completion_date}
                  onChange={ev => setCompletionDate(ev.target.value)}
                  variant="primary"
                  type="date"
                />
                <Text fontStyle="italic" p={2} fontSize="14px" color="#9E9AA3" fontWeight="400">
                  When the property was build or will be build by
                </Text>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Button variant="outline" mr={3} onClick={onClose} flex={1}>
              Cancel
            </Button>
            <Button
              flex={1}
              data-cy="create-properties"
              minWidth={"100px"}
              variant="solid"
              color={"#ffffff"}
              onClick={insertProperty}
              isLoading={loading}
            >
              Create Property
            </Button>
          </ModalFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default AddPropertyButton;
